/**
 * IRenderer의 Konva 구현체.
 *
 * - 스테이지·배경/시스템 레이어 생성(properties.ts 생성자), 노드 팩토리(`new Konva.X` 15종 → 핸들),
 *   tween(Konva.Tween)·ticker(Konva.Animation)·exportImage(index.ts captureJpeg의 Konva 부분)·
 *   stopAllDrags·justDragged(DD)·hints(KonvaPerfHints)·capabilities(Transformer·돋보기).
 * - Konva import는 backend/konva/ 와 lib/DocCanvas/ 에만 허용(eslint 봉인).
 */
import Konva from 'konva'
import { DD } from 'konva/lib/DragAndDrop'
import type { IFrame } from 'konva/lib/types'
import type {
  IArrow,
  ICircle,
  IEllipse,
  IGroup,
  IImage,
  ILayer,
  ILine,
  IMagnifierBackend,
  INode,
  IPath,
  IPathContext,
  IPerfHints,
  IRect,
  IRenderer,
  IRing,
  ISelectionHandles,
  IStage,
  IText,
  ITicker,
  ITween,
  PointerEventHandler,
  RendererCapabilities,
} from '../IRenderer'
import type {
  ArrowConfig,
  CircleConfig,
  Easing,
  EllipseConfig,
  ExportOptions,
  GroupConfig,
  ImageConfig,
  LineConfig,
  MagnifierConfig,
  PathConfig,
  RectConfig,
  RingConfig,
  SelectionHandlesConfig,
  ShapeConfig,
  TextConfig,
  TickerFrame,
  TweenOptions,
  TweenProps,
  Vector2d,
} from '../types'
import { KonvaEntityGroup } from './KonvaEntityGroup'
import { KonvaGeomHit, KonvaGeomHitGroup } from './KonvaGeomHit'
import { KonvaPerfHints } from './KonvaHints'
import { handleOf, unwrap } from './KonvaNode'
// Konva 9 프로토타입 패치(가져오기만 해도 적용된다)
import './KonvaLineSelfRect'
import './KonvaProtoListenerDedupe'
import './KonvaReadyDragEnd'
import './KonvaShapeFillStrokeCache'
import { buildBitmapPyramid, KonvaPyramidImage, type BitmapPyramid } from './bitmapPyramid'

type KonvaShapeAttrs = Konva.ShapeConfig

/** 사전 디코드 비트맵 상한(4096² ≈ 67MB) — 넘으면 기존 img 경로 */
const MAX_BITMAP_PIXELS = 4096 * 4096

/** ShapeConfig → Konva 속성(hitArea·perfectDraw 이름 번역). hitArea는 생성 뒤 핸들로 적용한다 */
function toKonvaShapeAttrs(cfg: ShapeConfig): KonvaShapeAttrs {
  const { hitArea: _hitArea, perfectDraw, ...rest } = cfg
  const attrs: KonvaShapeAttrs = { ...rest }
  if (perfectDraw !== undefined) attrs.perfectDrawEnabled = perfectDraw
  return attrs
}

function toKonvaEasing(easing: Easing): typeof Konva.Easings.Linear {
  switch (easing) {
    case 'easeInOut':
      return Konva.Easings.EaseInOut
    case 'easeOut':
      return Konva.Easings.EaseOut
    case 'linear':
      return Konva.Easings.Linear
  }
}

class KonvaTween implements ITween {
  constructor(private readonly tween: Konva.Tween) {}
  play(): void {
    this.tween.play()
  }
}

class KonvaTicker implements ITicker {
  constructor(private readonly animation: Konva.Animation) {}
  start(): void {
    this.animation.start()
  }
  stop(): void {
    this.animation.stop()
  }
  isRunning(): boolean {
    return this.animation.isRunning()
  }
}

/** Konva.Transformer 1:1 래퍼 */
class KonvaSelectionHandles implements ISelectionHandles {
  readonly transformer: Konva.Transformer
  readonly node: IGroup

  constructor(cfg: SelectionHandlesConfig, nodes?: INode[]) {
    this.transformer = new Konva.Transformer({
      ...cfg,
      nodes: nodes?.map(n => unwrap(n)),
    })
    this.node = handleOf(this.transformer)
  }

  attach(nodes: INode[]): void {
    this.transformer.nodes(nodes.map(n => unwrap(n)))
  }
  nodes(): INode[] {
    return this.transformer.nodes().map(n => handleOf(n))
  }
  detach(): void {
    this.transformer.nodes([])
  }
  destroy(): void {
    this.transformer.destroy()
  }
  config(partial: SelectionHandlesConfig): void {
    this.transformer.setAttrs(partial)
  }
  forceUpdate(): void {
    this.transformer.forceUpdate()
  }
  stopTransform(): void {
    this.transformer.stopTransform()
  }
  moveToTop(): void {
    this.transformer.moveToTop()
  }
  draggable(): boolean
  draggable(v: boolean): this
  draggable(v?: boolean): boolean | this {
    if (arguments.length === 0) return this.transformer.draggable()
    this.transformer.draggable(v as boolean)
    return this
  }
  on(evt: string, handler: PointerEventHandler): this {
    this.node.on(evt, handler)
    return this
  }
  off(evt: string): this {
    this.node.off(evt)
    return this
  }
  fire(evt: string): this {
    this.node.fire(evt)
    return this
  }
}

/**
 * 돋보기(handleController.ts 803~1150의 Konva 부분). 보이는 레이어의 자식을 clone해 클립 그룹에 넣는 방식.
 * 레이어는 첫 show 시점의 역배율(1/zoom)로 만들어 화면 고정 크기를 유지하고(원본 동작), 위치는 호출마다
 * 현재 줌을 곱한다. 위치 정책(뷰포트 경계·임계값)과 복제 대상 판정은 엔진이 가진다.
 */
class KonvaMagnifier implements IMagnifierBackend {
  private layer: Konva.Layer | null = null
  private background: Konva.Circle | null = null
  private clippedGroup: Konva.Group | null = null
  private magnifiedContent: Konva.Group | null = null
  private border: Konva.Circle | null = null
  visible = false

  constructor(
    private readonly stage: Konva.Stage,
    private readonly cfg: MagnifierConfig,
  ) {}

  private ensureLayer(): boolean {
    if (this.layer) return true
    const { radius, scale, borderColor, borderWidth } = this.cfg
    const inverseScale = 1 / this.stage.scaleX()
    this.layer = new Konva.Layer({
      visible: false,
      listening: false,
      scaleX: inverseScale,
      scaleY: inverseScale,
    })
    this.background = new Konva.Circle({ radius, fill: 'white', opacity: 0.9, listening: false })
    this.clippedGroup = new Konva.Group({
      clipFunc: (ctx): void => {
        ctx.arc(0, 0, radius, 0, Math.PI * 2, false)
      },
      listening: false,
    })
    this.magnifiedContent = new Konva.Group({ scaleX: scale, scaleY: scale, listening: false })
    this.border = new Konva.Circle({
      radius,
      stroke: borderColor,
      strokeWidth: borderWidth,
      fill: undefined,
      listening: false,
    })
    this.layer.add(this.background)
    this.layer.add(this.clippedGroup)
    this.layer.add(this.border)
    this.clippedGroup.add(this.magnifiedContent)
    this.stage.add(this.layer)
    return true
  }

  show(opts: { x: number; y: number; focusX: number; focusY: number }): void {
    if (!this.ensureLayer() || !this.layer) return
    const stageScale = this.stage.scaleX()
    const x = opts.x * stageScale
    const y = opts.y * stageScale
    this.background?.position({ x, y })
    this.clippedGroup?.position({ x, y })
    this.border?.position({ x, y })
    this.magnifiedContent?.position({
      x: -opts.focusX * this.cfg.scale,
      y: -opts.focusY * this.cfg.scale,
    })
    this.layer.visible(true)
    this.visible = true
    this.layer.batchDraw()
  }

  hide(): void {
    if (this.layer) {
      this.layer.visible(false)
      this.layer.batchDraw()
    }
    this.visible = false
  }

  refreshContent(include: (node: INode, layer: ILayer) => boolean): void {
    // 원본은 초기화 시점에 레이어를 만들어 첫 표시 전 내용 갱신도 반영됐다 — 여기서도 먼저 만든다
    this.ensureLayer()
    const content = this.magnifiedContent
    if (!content) return
    try {
      content.destroyChildren()
      this.stage.getLayers().forEach((layer: Konva.Layer) => {
        if (layer === this.layer || !layer.visible()) return
        const layerHandle = handleOf(layer)
        try {
          layer.getChildren().forEach((node: Konva.Node) => {
            try {
              if (!include(handleOf(node), layerHandle)) return
              const cloned = node.clone()
              cloned.listening(false)
              content.add(cloned)
            } catch (_nodeError) {
              // 개별 노드 복제 실패 시 무시
            }
          })
        } catch (_layerError) {
          // 레이어 처리 실패 시 무시
        }
      })
      content.getLayer()?.batchDraw()
    } catch (error) {
      console.warn('Failed to update magnifier content:', error)
    }
  }

  destroy(): void {
    this.layer?.destroy()
    this.layer = null
    this.background = null
    this.clippedGroup = null
    this.magnifiedContent = null
    this.border = null
    this.visible = false
  }
}

export class KonvaRenderer implements IRenderer {
  readonly kind = 'konva' as const
  readonly stage: IStage
  readonly layers: { readonly background: ILayer; readonly system: ILayer }
  readonly hints: IPerfHints
  readonly capabilities: RendererCapabilities
  /** 기하 hit(브러시 획 판정) */
  readonly geomHit: KonvaGeomHit

  private constructor(
    readonly konvaStage: Konva.Stage,
    readonly konvaBackgroundLayer: Konva.Layer,
    readonly konvaSystemLayer: Konva.Layer,
  ) {
    this.stage = handleOf(konvaStage)
    this.layers = {
      background: handleOf(konvaBackgroundLayer),
      system: handleOf(konvaSystemLayer),
    }
    this.hints = new KonvaPerfHints(konvaStage)
    this.geomHit = new KonvaGeomHit(konvaStage)
    this.capabilities = {
      selectionHandles: (cfg, nodes): ISelectionHandles => new KonvaSelectionHandles(cfg, nodes),
      magnifier: (cfg): IMagnifierBackend => new KonvaMagnifier(konvaStage, cfg),
    }
  }

  /** 새 스테이지 + 배경/시스템 레이어(properties.ts 생성자와 동일 구성) */
  static create(opts: {
    container: string | HTMLDivElement
    width: number
    height: number
  }): KonvaRenderer {
    const stage = new Konva.Stage({
      container: opts.container,
      width: opts.width,
      height: opts.height,
      draggable: false,
    })
    const background = new Konva.Layer()
    const system = new Konva.Layer()
    stage.add(background)
    stage.add(system)
    const renderer = new KonvaRenderer(stage, background, system)
    // 드래그 중에도 hit 갱신(properties.ts 모듈 로드 시 설정과 동일)
    renderer.hints.setOption('hitOnDragEnabled', true)
    return renderer
  }

  createOverlayLayer(): ILayer {
    const layer = new Konva.Layer()
    this.konvaStage.add(layer)
    return handleOf(layer)
  }

  destroy(): void {
    this.konvaStage.destroy()
  }

  // ---- 노드 팩토리 ----

  createGroup(cfg: GroupConfig = {}): IGroup {
    const { entityRoot, hitCanvas, ...attrs } = cfg
    if (hitCanvas === false) {
      const group = new KonvaGeomHitGroup(attrs)
      this.geomHit.register(group)
      return handleOf(group)
    }
    const group = entityRoot ? new KonvaEntityGroup(attrs) : new Konva.Group(attrs)
    return handleOf(group)
  }

  createRect(cfg: RectConfig = {}): IRect {
    const rect = new Konva.Rect(toKonvaShapeAttrs(cfg))
    return this.withHitArea(handleOf(rect) as IRect, cfg)
  }

  createEllipse(cfg: EllipseConfig): IEllipse {
    const ellipse = new Konva.Ellipse(toKonvaShapeAttrs(cfg) as Konva.EllipseConfig)
    return this.withHitArea(handleOf(ellipse) as IEllipse, cfg)
  }

  createCircle(cfg: CircleConfig): ICircle {
    const circle = new Konva.Circle(toKonvaShapeAttrs(cfg))
    return this.withHitArea(handleOf(circle) as ICircle, cfg)
  }

  createRing(cfg: RingConfig): IRing {
    const ring = new Konva.Ring(toKonvaShapeAttrs(cfg) as Konva.RingConfig)
    return this.withHitArea(handleOf(ring) as IRing, cfg)
  }

  createLine(cfg: LineConfig): ILine {
    const line = new Konva.Line(toKonvaShapeAttrs(cfg) as Konva.LineConfig)
    return this.withHitArea(handleOf(line) as ILine, cfg)
  }

  createArrow(cfg: ArrowConfig): IArrow {
    const arrow = new Konva.Arrow(toKonvaShapeAttrs(cfg) as Konva.ArrowConfig)
    return this.withHitArea(handleOf(arrow) as IArrow, cfg)
  }

  createText(cfg: TextConfig): IText {
    const text = new Konva.Text(toKonvaShapeAttrs(cfg))
    return this.withHitArea(handleOf(text) as IText, cfg)
  }

  createImage(cfg: ImageConfig = {}): IImage {
    const image = new Konva.Image(toKonvaShapeAttrs(cfg) as Konva.ImageConfig)
    return this.withHitArea(handleOf(image) as IImage, cfg)
  }

  loadImage(url: string, cfg: ImageConfig = {}, predecode?: boolean): Promise<IImage> {
    if (predecode) {
      // 사전 디코드 실패(네트워크·CORS·미지원 등)는 기존 경로로 — 그리기 때 동기 디코드될 뿐 결과는 같다
      return this.loadImageBitmap(url).then(
        source => {
          const image =
            source instanceof ImageBitmap
              ? new Konva.Image({ ...toKonvaShapeAttrs(cfg), image: source })
              : new KonvaPyramidImage(toKonvaShapeAttrs(cfg) as Konva.ImageConfig, source)
          return this.withHitArea(handleOf(image) as IImage, cfg)
        },
        () => this.loadImage(url, cfg),
      )
    }
    return new Promise<IImage>((resolve, reject) => {
      Konva.Image.fromURL(
        url,
        (image: Konva.Image) => {
          image.setAttrs(toKonvaShapeAttrs(cfg))
          resolve(this.withHitArea(handleOf(image) as IImage, cfg))
        },
        (error: unknown) => reject(error),
      )
    })
  }

  private async loadImageBitmap(url: string): Promise<ImageBitmap | BitmapPyramid> {
    // HTMLImageElement 소스(img.decode() 포함)는 그리기 때 메인 스레드에서 디코드돼 Blob 소스로(Chromium은 워커 스레드 디코드)
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' })
    if (!res.ok) throw new Error(`이미지 응답 ${res.status}`)
    const blob = await res.blob()
    // 축소본 피라미드(워커) — 못 만들면 원본 비트맵만
    const pyramid = await buildBitmapPyramid(blob)
    const bitmap =
      pyramid?.full ?? (await createImageBitmap(blob, { imageOrientation: 'from-image' }))
    // 비트맵은 메모리 압박에도 버려지지 않으므로 아주 큰 원본은 기존 경로(버릴 수 있는 디코드 캐시)로
    if (bitmap.width * bitmap.height > MAX_BITMAP_PIXELS) {
      bitmap.close()
      pyramid?.half.close()
      pyramid?.quarter.close()
      throw new Error('이미지가 너무 커 사전 디코드 생략')
    }
    return pyramid ?? bitmap
  }

  createPath(cfg: PathConfig, draw: (ctx: IPathContext, self: IPath) => void): IPath {
    const shape = new Konva.Shape(toKonvaShapeAttrs(cfg))
    const path = handleOf(shape) as IPath
    path.redraw(draw)
    return this.withHitArea(path, cfg)
  }

  private withHitArea<
    T extends IRect | IEllipse | ICircle | IRing | ILine | IArrow | IText | IImage | IPath,
  >(shape: T, cfg: ShapeConfig): T {
    if (cfg.hitArea) shape.hitArea(cfg.hitArea)
    return shape
  }

  // ---- 그리기 ----

  requestDraw(layer?: ILayer): void {
    if (layer) unwrap(layer, Konva.Layer).batchDraw()
    else this.konvaStage.batchDraw()
  }

  drawNow(layer?: ILayer): void {
    if (layer) unwrap(layer, Konva.Layer).draw()
    else this.konvaStage.draw()
  }

  // ---- 입력·히트 ----

  getPointerPosition(): Vector2d | null {
    return this.konvaStage.getPointerPosition()
  }

  /**
   * 진행 중인 모든 드래그 중단. 엔진 원본(handleController stopAllDrags)은 `stage.find('*')`를 썼는데
   * Konva에서 '*'는 빈 배열이라 실제로는 아무 노드도 멈추지 않았다.
   * 여기서는 의도대로 전 노드를 순회한다.
   */
  stopAllDrags(): void {
    if (this.konvaStage.isDragging()) this.konvaStage.stopDrag()
    this.konvaStage.find((node: Konva.Node) => node.isDragging()).forEach(node => node.stopDrag())
  }

  hitTest(pos: Vector2d): INode | null {
    const shape = this.konvaStage.getIntersection(pos)
    return shape ? handleOf(shape) : null
  }

  justDragged(): boolean {
    return DD.justDragged
  }

  // ---- 애니메이션 ----

  tween(node: INode, to: TweenProps, opts: TweenOptions): ITween {
    const tween = new Konva.Tween({
      node: unwrap(node),
      duration: opts.duration,
      easing: toKonvaEasing(opts.easing),
      onFinish: opts.onFinish,
      ...to,
    })
    return new KonvaTween(tween)
  }

  ticker(frame: (f: TickerFrame | undefined) => void, layers?: ILayer[]): ITicker {
    const animation = new Konva.Animation(
      (f?: IFrame) => {
        frame(f ? { time: f.time, timeDiff: f.timeDiff } : undefined)
      },
      layers?.map(layer => unwrap(layer, Konva.Layer)),
    )
    return new KonvaTicker(animation)
  }

  // ---- 출력 ----

  exportImage(opts: ExportOptions & { type: 'base64' }): Promise<string>
  exportImage(opts: ExportOptions & { type: 'blob' }): Promise<Blob>
  async exportImage(opts: ExportOptions): Promise<string | Blob> {
    const stage = this.konvaStage
    const viewport = { x: stage.x(), y: stage.y(), scaleX: stage.scaleX(), scaleY: stage.scaleY() }
    // 변환 1:1 리셋(index.ts captureJpeg와 동일 순서)
    stage.x(0)
    stage.y(0)
    stage.scaleX(1)
    stage.scaleY(1)
    let backdrop: Konva.Rect | null = null
    if (opts.backdropFill) {
      backdrop = new Konva.Rect({
        x: opts.x,
        y: opts.y,
        width: opts.width,
        height: opts.height,
        fill: opts.backdropFill,
      })
      this.konvaBackgroundLayer.add(backdrop)
      backdrop.moveToBottom()
      this.konvaBackgroundLayer.draw()
      this.konvaBackgroundLayer.moveToBottom()
    }
    const config = {
      mimeType: opts.mimeType,
      quality: opts.quality,
      pixelRatio: opts.pixelRatio,
      x: opts.x,
      y: opts.y,
      width: opts.width,
      height: opts.height,
    }
    try {
      if (opts.type === 'base64') return stage.toDataURL(config)
      return await (stage.toBlob(config) as Promise<Blob>)
    } finally {
      stage.x(viewport.x)
      stage.y(viewport.y)
      stage.scaleX(viewport.scaleX)
      stage.scaleY(viewport.scaleY)
      backdrop?.destroy()
    }
  }
}
